import { useCallback, useMemo, useState } from 'react';

import APSideSheet from '@shared/components/organisms/side-sheet';

import type { ISideSheetConfig } from './types';
import type { TSideSheetSide } from '@shared/components/organisms/side-sheet/types';
import type { FC, PropsWithChildren, ReactNode } from 'react';

import { SideSheetContext } from './context';

type SideSheetState = Omit<Partial<ISideSheetConfig>, 'content'> & {
  isOpen: boolean;
  content: ReactNode | null;
  side: TSideSheetSide;
  showBackdrop: boolean;
  showCloseButton: boolean;
  closeOnBackdropClick: boolean;
  width?: number | string;
};

const DEFAULT_STATE: SideSheetState = {
  isOpen: false,
  content: null,
  footer: undefined,
  slots: undefined,
  side: 'right',
  width: 480,
  showBackdrop: true,
  showCloseButton: true,
  closeOnBackdropClick: true,
  title: undefined,
  subtitle: undefined,
  headerSlot: undefined,
  actions: undefined,
  className: undefined,
  ariaLabel: undefined,
  ariaDescribedBy: undefined,
  onClose: undefined,
};

export const SideSheetProvider: FC<PropsWithChildren> = ({ children }) => {
  const [sheetState, setSheetState] = useState<SideSheetState>(DEFAULT_STATE);

  const openSideSheet = useCallback((config: ISideSheetConfig) => {
    setSheetState({
      ...DEFAULT_STATE,
      ...config,
      content: config.content,
      footer: config.footer,
      slots: config.slots,
      side: config.side ?? 'right',
      width: config.width ?? DEFAULT_STATE.width,
      showBackdrop: config.showBackdrop ?? true,
      showCloseButton: config.showCloseButton ?? true,
      closeOnBackdropClick: config.closeOnBackdropClick ?? true,
      isOpen: true,
    });
  }, []);

  const closeSideSheet = useCallback(() => {
    setSheetState((prev) => {
      prev.onClose?.();
      return { ...prev, isOpen: false };
    });
  }, []);

  const updateSideSheet = useCallback((config: Partial<ISideSheetConfig>) => {
    setSheetState((prev) => ({
      ...prev,
      title: config.title ?? prev.title,
      subtitle: config.subtitle ?? prev.subtitle,
      headerSlot: config.headerSlot ?? prev.headerSlot,
      actions: config.actions ?? prev.actions,
      content: config.content ?? prev.content,
      footer: config.footer ?? prev.footer,
      slots: config.slots ?? prev.slots,
      side: config.side ?? prev.side,
      width: config.width ?? prev.width,
      showBackdrop: config.showBackdrop ?? prev.showBackdrop,
      showCloseButton: config.showCloseButton ?? prev.showCloseButton,
      closeOnBackdropClick: config.closeOnBackdropClick ?? prev.closeOnBackdropClick,
      className: config.className ?? prev.className,
      ariaLabel: config.ariaLabel ?? prev.ariaLabel,
      ariaDescribedBy: config.ariaDescribedBy ?? prev.ariaDescribedBy,
      onClose: config.onClose ?? prev.onClose,
      'data-testid': config['data-testid'] ?? prev['data-testid'],
    }));
  }, []);

  const contextValue = useMemo(
    () => ({
      openSideSheet,
      closeSideSheet,
      updateSideSheet,
      isOpen: sheetState.isOpen,
      currentSide: sheetState.side,
    }),
    [closeSideSheet, openSideSheet, sheetState.isOpen, sheetState.side, updateSideSheet]
  );

  return (
    <SideSheetContext.Provider value={contextValue}>
      {children}
      <APSideSheet
        isOpen={sheetState.isOpen}
        onClose={closeSideSheet}
        side={sheetState.side}
        title={sheetState.title}
        subtitle={sheetState.subtitle}
        headerSlot={sheetState.headerSlot}
        actions={sheetState.actions}
        width={sheetState.width}
        showCloseButton={sheetState.showCloseButton}
        showBackdrop={sheetState.showBackdrop}
        closeOnBackdropClick={sheetState.closeOnBackdropClick}
        className={sheetState.className}
        ariaLabel={sheetState.ariaLabel}
        ariaDescribedBy={sheetState.ariaDescribedBy}
        slots={sheetState.slots}
        content={sheetState.content}
        footer={sheetState.footer}
        data-testid={sheetState['data-testid']}
      />
    </SideSheetContext.Provider>
  );
};

export default SideSheetProvider;
